import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import {
  UserCog, Plus, Edit, XCircle, CheckCircle2, Shield,
  Eye, EyeOff, KeyRound, AlertCircle, ToggleLeft, ToggleRight, Mail, Save, Info
} from 'lucide-react';

const ROL_LABELS = {
  administrador: 'Administrador',
  directora_tecnica: 'Directora Técnica',
  almacen: 'Almacén',
  administracion: 'Administración',
  consulta: 'Consulta / Auditoría'
};
const ROL_COLORS = {
  administrador: '#e91e8c', directora_tecnica: '#9c27b0',
  almacen: '#2196f3', administracion: '#ff9800', consulta: '#607d8b'
};

// ═══════════════════════════════════════════════════════════════
// PANEL DE CONFIGURACIÓN DE EMAILS
// ═══════════════════════════════════════════════════════════════
function PanelEmails() {
  const [config, setConfig] = useState({
    emails_solicitud_firma: '',
    emails_confirmacion_firma: '',
    emails_rechazo_firma: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/firmas/config-emails').then(r => {
      const map = {};
      r.data.forEach(item => { map[item.clave] = item.valor || ''; });
      setConfig(prev => ({ ...prev, ...map }));
    }).finally(() => setLoading(false));
  }, []);

  const handleGuardar = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      await api.put('/firmas/config-emails', config);
      setMsg('✅ Configuración de emails guardada correctamente');
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-overlay"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Explicación general */}
      <div style={{ background: 'var(--color-info-bg)', border: '1px solid #93c5fd', borderRadius: 8, padding: 16, display: 'flex', gap: 12 }}>
        <Info size={18} color="var(--color-info)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: '0.875rem', color: '#1e40af', lineHeight: 1.6 }}>
          <strong>Configura aquí los emails del flujo de firma.</strong> Puedes poner varios emails separados por coma.<br />
          Si los campos están vacíos, el sistema usará el email de la Directora Técnica (para solicitudes) y el del Administrador (para confirmaciones).
        </div>
      </div>

      {msg && <div className="alert alert-success"><CheckCircle2 size={16} /> {msg}</div>}
      {error && <div className="alert alert-danger"><AlertCircle size={16} /> {error}</div>}

      {/* Tarjeta 1: Solicitud de firma */}
      <div className="card">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #fff9fd, #fce4f3)' }}>
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.2rem' }}>✍️</span> Email de Solicitud de Firma
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
              Se envía cuando alguien pulsa <strong>"Solicitar Firma"</strong> en un documento.
              Normalmente es el email de la Directora Técnica.
            </p>
          </div>
        </div>
        <div className="card-body">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={14} color="var(--color-primary)" />
              Email(s) que reciben la solicitud de firma
            </label>
            <input
              className="form-control"
              value={config.emails_solicitud_firma}
              onChange={e => setConfig(c => ({ ...c, emails_solicitud_firma: e.target.value }))}
              placeholder="directora@lomhifar.com, tecnica@lomhifar.com"
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 6 }}>
              💡 El email incluye el resumen del documento, la guía de firma paso a paso y el botón directo al sistema.
            </p>
          </div>
        </div>
      </div>

      {/* Tarjeta 2: Confirmación de firma */}
      <div className="card">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' }}>
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span> Email de Confirmación de Firma
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
              Se envía automáticamente cuando la Directora Técnica <strong>firma un documento</strong>.
              Normalmente es el email del Administrador o Gerencia.
            </p>
          </div>
        </div>
        <div className="card-body">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={14} color="var(--color-success)" />
              Email(s) que reciben la confirmación de firma
            </label>
            <input
              className="form-control"
              value={config.emails_confirmacion_firma}
              onChange={e => setConfig(c => ({ ...c, emails_confirmacion_firma: e.target.value }))}
              placeholder="admin@lomhifar.com, gerencia@lomhifar.com"
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 6 }}>
              💡 El email incluye el bloque de firma electrónica completo con nombre, cargo, fecha y hora.
            </p>
          </div>
        </div>
      </div>

      {/* Tarjeta 3: Rechazo */}
      <div className="card">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #fff5f5, #fee2e2)' }}>
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.2rem' }}>❌</span> Email de Rechazo de Documento
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
              Se envía cuando la Directora Técnica <strong>rechaza un documento</strong> con comentarios.
              Normalmente es el mismo que la confirmación de firma.
            </p>
          </div>
        </div>
        <div className="card-body">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={14} color="var(--color-danger)" />
              Email(s) que reciben el aviso de rechazo
            </label>
            <input
              className="form-control"
              value={config.emails_rechazo_firma}
              onChange={e => setConfig(c => ({ ...c, emails_rechazo_firma: e.target.value }))}
              placeholder="admin@lomhifar.com, responsable@lomhifar.com"
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 6 }}>
              💡 El email incluye el motivo del rechazo y las instrucciones para corregir y volver a solicitar la firma.
            </p>
          </div>
        </div>
      </div>

      {/* Resumen visual */}
      <div style={{ background: 'var(--color-gray-1)', borderRadius: 10, padding: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 14, fontSize: '0.875rem' }}>
          📊 Flujo completo de firma por email
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
          {[
            { color: '#2196f3', texto: 'Alguien pulsa\n"Solicitar Firma"', sub: '' },
            { arrow: true },
            { color: '#e91e8c', texto: 'Email enviado a:', sub: config.emails_solicitud_firma || '(Directora Técnica)' },
            { arrow: true },
            { color: '#9c27b0', texto: 'Directora revisa\ny firma o rechaza', sub: '' },
            { arrow: true },
            { color: '#16a34a', texto: 'Si firma →\nEmail a:', sub: config.emails_confirmacion_firma || '(Administrador)' },
            { arrow: true },
            { color: '#dc2626', texto: 'Si rechaza →\nEmail a:', sub: config.emails_rechazo_firma || '(Administrador)' },
          ].map((item, i) => item.arrow ? (
            <div key={i} style={{ color: 'var(--color-text-3)', fontSize: '1.2rem', padding: '0 6px' }}>→</div>
          ) : (
            <div key={i} style={{ background: `${item.color}15`, border: `1.5px solid ${item.color}40`, borderRadius: 8, padding: '10px 14px', minWidth: 110, textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: item.color, lineHeight: 1.4, whiteSpace: 'pre-line' }}>{item.texto}</div>
              {item.sub && <div style={{ fontSize: '0.68rem', color: 'var(--color-text-3)', marginTop: 4, wordBreak: 'break-all' }}>{item.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary btn-lg" onClick={handleGuardar} disabled={saving}>
          {saving ? <div className="spinner" style={{ width: 18, height: 18 }} /> : <Save size={18} />}
          {saving ? 'Guardando...' : 'Guardar configuración de emails'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PANEL DE PERMISOS POR ROL
// ═══════════════════════════════════════════════════════════════
function PanelPermisos({ roles, onActualizado }) {
  const [saving, setSaving] = useState(null);

  const handleToggle = async (rol, permiso) => {
    if (rol.nombre === 'administrador') return;
    setSaving(rol.id);
    try {
      await api.put(`/usuarios/roles/${rol.id}`, { ...rol, [permiso]: rol[permiso] ? 0 : 1 });
      onActualizado();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setSaving(null); }
  };

  const PERMISOS = [
    { key: 'puede_firmar',          label: '✍️ Firmar documentos',      desc: 'Firmar electrónicamente PNTs',          critico: true },
    { key: 'puede_crear_pnt',       label: '📄 Crear y editar PNTs',    desc: 'Crear y modificar procedimientos' },
    { key: 'puede_subir_archivos',  label: '📎 Subir archivos',         desc: 'Adjuntar documentos al sistema' },
    { key: 'puede_ver_todos',       label: '👁 Ver todos los documentos',desc: 'Acceso completo de lectura' },
    { key: 'puede_exportar',        label: '📥 Exportar e imprimir',    desc: 'Generar informes PDF' },
  ];

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={16} color="var(--color-primary)" /> Permisos por Rol
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
            Haz clic en el interruptor para activar o desactivar cada permiso. Los cambios tienen efecto inmediato.
          </p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Permiso</th>
              {roles.map(r => (
                <th key={r.id} style={{ textAlign: 'center', minWidth: 130 }}>
                  <div style={{ color: ROL_COLORS[r.nombre], fontWeight: 700 }}>{ROL_LABELS[r.nombre] || r.nombre}</div>
                  {r.nombre === 'administrador' && (
                    <div style={{ fontSize: '0.65rem', color: 'var(--color-text-3)', fontWeight: 400 }}>siempre activo</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISOS.map(p => (
              <tr key={p.key}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.label}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>{p.desc}</div>
                  {p.critico && <div style={{ fontSize: '0.68rem', color: 'var(--color-danger)', fontWeight: 600, marginTop: 2 }}>🔒 Permiso crítico</div>}
                </td>
                {roles.map(r => {
                  const tiene = r[p.key] === 1 || r[p.key] === true;
                  const inmutable = r.nombre === 'administrador';
                  return (
                    <td key={r.id} style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => !inmutable && handleToggle(r, p.key)}
                        disabled={inmutable || saving === r.id}
                        title={inmutable ? 'El administrador siempre tiene todos los permisos' : tiene ? 'Clic para quitar' : 'Clic para activar'}
                        style={{ background: 'none', border: 'none', cursor: inmutable ? 'default' : 'pointer', opacity: inmutable ? 0.35 : 1, padding: 4 }}
                      >
                        {saving === r.id
                          ? <div className="spinner" style={{ width: 20, height: 20 }} />
                          : tiene
                            ? <ToggleRight size={30} color="var(--color-success)" />
                            : <ToggleLeft size={30} color="var(--color-gray-3)" />
                        }
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '12px 20px', background: 'var(--color-gray-1)', borderTop: '1px solid var(--color-gray-3)', fontSize: '0.78rem', color: 'var(--color-text-3)' }}>
        🔒 El permiso <strong>"Firmar documentos"</strong> solo debe estar activo para la <strong>Directora Técnica</strong>.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL CREAR / EDITAR USUARIO
// ═══════════════════════════════════════════════════════════════
function ModalUsuario({ usuario, roles, onClose, onGuardado }) {
  const [form, setForm] = useState(usuario || {
    nombre: '', apellidos: '', username: '', email: '',
    password: '', rol_id: '', activo: true, email_notificaciones: ''
  });
  const [permisos, setPermisos] = useState({
    perm_firmar: usuario?.perm_firmar ?? null,
    perm_crear_pnt: usuario?.perm_crear_pnt ?? null,
    perm_subir_archivos: usuario?.perm_subir_archivos ?? null,
    perm_ver_todos: usuario?.perm_ver_todos ?? null,
    perm_exportar: usuario?.perm_exportar ?? null,
  });
  // Suplente de firma: sí/no simple del propio usuario (no tri-estado)
  const [esSuplenteFirma, setEsSuplenteFirma] = useState(
    usuario?.es_suplente_firma === 1 || usuario?.es_suplente_firma === true
  );
  const [tabModal, setTabModal] = useState('datos');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingPermisos, setSavingPermisos] = useState(false);
  const [error, setError] = useState('');
  const [msgPermisos, setMsgPermisos] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const rolSeleccionado = roles.find(r => r.id === parseInt(form.rol_id));
  const esAdmin = rolSeleccionado?.nombre === 'administrador';

  const PERMISOS_DEF = [
    { key: 'perm_firmar',          rolKey: 'puede_firmar',         label: '✍️ Firmar documentos',       desc: 'Firmar electrónicamente PNTs', critico: true },
    { key: 'perm_crear_pnt',       rolKey: 'puede_crear_pnt',      label: '📄 Crear y editar PNTs',     desc: 'Crear y modificar procedimientos' },
    { key: 'perm_subir_archivos',  rolKey: 'puede_subir_archivos', label: '📎 Subir archivos',          desc: 'Adjuntar documentos al sistema' },
    { key: 'perm_ver_todos',       rolKey: 'puede_ver_todos',      label: '👁 Ver todos los documentos',desc: 'Acceso completo de lectura' },
    { key: 'perm_exportar',        rolKey: 'puede_exportar',       label: '📥 Exportar e imprimir',     desc: 'Generar informes PDF' },
  ];

  const guardar = async () => {
    if (!form.nombre || !form.email || !form.rol_id) { setError('Nombre, email y rol son obligatorios'); return; }
    if (!form.id && !form.password) { setError('La contraseña inicial es obligatoria'); return; }
    setLoading(true); setError('');
    try {
      let resp;
      if (form.id) {
        resp = await api.put(`/usuarios/${form.id}`, form);
      } else {
        resp = await api.post('/usuarios', form);
      }
      // Guardar permisos individuales si alguno fue modificado.
      // El Administrador tiene los permisos protegidos en el backend:
      // NO intentamos guardar sus permisos (daría el error "No se pueden
      // modificar los permisos del Administrador" y bloquearía el guardado
      // de sus datos, incluida la contraseña). Sus datos sí se guardan.
      const hayPermisos = Object.values(permisos).some(v => v !== null);
      const userId = form.id || resp.data?.id;
      if (userId && !esAdmin) {
        // Siempre enviamos es_suplente_firma junto con los permisos
        if (hayPermisos) {
          await api.put(`/usuarios/${userId}/permisos`, { ...permisos, es_suplente_firma: esSuplenteFirma ? 1 : 0 });
        } else {
          await api.put(`/usuarios/${userId}/permisos`, { es_suplente_firma: esSuplenteFirma ? 1 : 0 });
        }
      }
      onGuardado();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    finally { setLoading(false); }
  };

  const guardarPermisos = async () => {
    if (!form.id) return;
    setSavingPermisos(true); setMsgPermisos('');
    try {
      await api.put(`/usuarios/${form.id}/permisos`, { ...permisos, es_suplente_firma: esSuplenteFirma ? 1 : 0 });
      setMsgPermisos('✅ Permisos guardados correctamente');
      setTimeout(() => setMsgPermisos(''), 3000);
    } catch (e) {
      setMsgPermisos('❌ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally { setSavingPermisos(false); }
  };

  const getPermValue = (key) => {
    const val = permisos[key];
    if (val === null || val === undefined) return null; // heredar rol
    return val === 1 || val === true;
  };

  const getRolValue = (rolKey) => {
    if (!rolSeleccionado) return false;
    return rolSeleccionado[rolKey] === 1 || rolSeleccionado[rolKey] === true;
  };

  const getEfectivo = (key, rolKey) => {
    const v = getPermValue(key);
    return v === null ? getRolValue(rolKey) : v;
  };

  const togglePerm = (key) => {
    const actual = getPermValue(key);
    const rolVal = getRolValue(PERMISOS_DEF.find(p => p.key === key)?.rolKey);
    if (actual === null) {
      // Heredando → forzar lo contrario del rol
      setPermisos(p => ({ ...p, [key]: rolVal ? 0 : 1 }));
    } else if (actual === true || actual === 1) {
      setPermisos(p => ({ ...p, [key]: 0 }));
    } else {
      // estaba en false → volver a heredar si coincide con rol, sino true
      setPermisos(p => ({ ...p, [key]: null }));
    }
  };

  const resetPerm = (key) => setPermisos(p => ({ ...p, [key]: null }));

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserCog size={18} color="var(--color-primary)" />
            {form.id ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>

        {/* Tabs del modal */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--color-gray-3)', padding: '0 24px' }}>
          {[
            { key: 'datos', label: '👤 Datos' },
            { key: 'permisos', label: '🛡 Permisos individuales' },
          ].map(t => (
            <button key={t.key} onClick={() => setTabModal(t.key)} style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tabModal === t.key ? 700 : 400,
              color: tabModal === t.key ? 'var(--color-primary)' : 'var(--color-text-3)',
              borderBottom: tabModal === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: -2, fontFamily: 'var(--font)'
            }}>{t.label}</button>
          ))}
        </div>

        <div className="modal-body">

          {/* ── TAB DATOS ── */}
          {tabModal === 'datos' && <>
            {error && <div className="alert alert-danger"><AlertCircle size={15} /> {error}</div>}

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombre <span className="required">*</span></label>
                <input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Apellidos</label>
                <input className="form-control" value={form.apellidos || ''} onChange={e => set('apellidos', e.target.value)} />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombre de usuario {!form.id && <span className="required">*</span>}</label>
                <input className="form-control" style={{ fontFamily: 'var(--font-mono)' }}
                  value={form.username || ''} onChange={e => set('username', e.target.value)} disabled={!!form.id} />
                {form.id && <p style={{ fontSize: '0.73rem', color: 'var(--color-text-3)', marginTop: 4 }}>No se puede cambiar</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Email principal <span className="required">*</span></label>
                <input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Mail size={13} color="var(--color-primary)" />
                Email para notificaciones (opcional)
              </label>
              <input type="email" className="form-control"
                value={form.email_notificaciones || ''}
                onChange={e => set('email_notificaciones', e.target.value)}
                placeholder="Si es diferente al email de acceso" />
              <p style={{ fontSize: '0.73rem', color: 'var(--color-text-3)', marginTop: 4 }}>
                Las notificaciones de firma se enviarán a este email. Si está vacío, se usa el email principal.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                {form.id ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña inicial *'}
              </label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} className="form-control" style={{ paddingRight: 40 }}
                  value={form.password || ''} onChange={e => set('password', e.target.value)}
                  placeholder={form.id ? 'Dejar vacío para no cambiar' : 'Mínimo 8 caracteres'} />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!form.id && <p style={{ fontSize: '0.73rem', color: 'var(--color-warning)', marginTop: 4 }}>
                ⚠ El usuario deberá cambiar esta contraseña en su primer acceso.
              </p>}
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Rol / Nivel de acceso <span className="required">*</span></label>
                <select className="form-control" value={form.rol_id} onChange={e => set('rol_id', e.target.value)}>
                  <option value="">— Seleccionar rol —</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{ROL_LABELS[r.nombre] || r.nombre}</option>)}
                </select>
              </div>
              {form.id && (
                <div className="form-group">
                  <label className="form-label">Estado de la cuenta</label>
                  <select className="form-control" value={form.activo ? '1' : '0'} onChange={e => set('activo', e.target.value === '1')}>
                    <option value="1">✅ Cuenta activa</option>
                    <option value="0">🔒 Cuenta desactivada</option>
                  </select>
                </div>
              )}
            </div>
          </>}

          {/* ── TAB PERMISOS INDIVIDUALES ── */}
          {tabModal === 'permisos' && (
            <div>
              {msgPermisos && (
                <div className={`alert ${msgPermisos.startsWith('✅') ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: 16 }}>
                  {msgPermisos}
                </div>
              )}

              {esAdmin ? (
                <div className="alert alert-info">
                  <Shield size={16} /> El Administrador siempre tiene todos los permisos activados y no se pueden modificar.
                </div>
              ) : (
                <>
                  <div style={{ background: 'var(--color-info-bg)', border: '1px solid #93c5fd', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: '0.82rem', color: '#1e40af' }}>
                    {form.id ? (
                      <><strong>💡 Permisos individuales</strong> — Estos permisos son específicos para <strong>{form.nombre} {form.apellidos}</strong> y tienen prioridad sobre los del rol. Si están en "Heredar del rol", se aplican los permisos del rol asignado.</>
                    ) : (
                      <><strong>💡 Permisos para el nuevo usuario</strong> — Configura permisos específicos que tendrán prioridad sobre los del rol. Si los dejas en "Heredar del rol", el usuario tendrá los permisos estándar del rol seleccionado en Datos.</>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {PERMISOS_DEF.map(p => {
                      const val = getPermValue(p.key);
                      const efectivo = getEfectivo(p.key, p.rolKey);
                      const heredando = val === null;
                      return (
                        <div key={p.key} style={{
                          display: 'flex', alignItems: 'center', gap: 16,
                          padding: '14px 16px', borderRadius: 10,
                          background: efectivo ? '#f0fdf4' : 'var(--color-gray-1)',
                          border: `1.5px solid ${efectivo ? '#86efac' : 'var(--color-gray-3)'}`,
                        }}>
                          {/* Toggle */}
                          <button onClick={() => togglePerm(p.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
                            {efectivo
                              ? <ToggleRight size={36} color="var(--color-success)" />
                              : <ToggleLeft size={36} color="var(--color-gray-3)" />
                            }
                          </button>

                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.label}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>{p.desc}</div>
                            {p.critico && <div style={{ fontSize: '0.68rem', color: 'var(--color-danger)', fontWeight: 600 }}>🔒 Permiso crítico</div>}
                          </div>

                          {/* Estado */}
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {heredando ? (
                              <span style={{ fontSize: '0.72rem', background: 'var(--color-gray-2)', color: 'var(--color-text-3)', padding: '3px 8px', borderRadius: 20, fontWeight: 600 }}>
                                Heredado del rol {efectivo ? '✓' : '✗'}
                              </span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: 'column' }}>
                                <span style={{ fontSize: '0.72rem', background: efectivo ? 'var(--color-success-bg)' : 'var(--color-danger-bg)', color: efectivo ? 'var(--color-success)' : 'var(--color-danger)', padding: '3px 8px', borderRadius: 20, fontWeight: 700 }}>
                                  {efectivo ? '✓ Activado' : '✗ Desactivado'}
                                </span>
                                <button onClick={() => resetPerm(p.key)} style={{ fontSize: '0.65rem', color: 'var(--color-text-3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Heredar del rol
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Casilla especial: Suplente de firma (trazable) */}
                  {!esAdmin && (
                    <div style={{
                      marginTop: 18,
                      padding: '14px 16px',
                      background: '#fef9c3',
                      border: '1px solid #eab308',
                      borderRadius: 10,
                    }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={esSuplenteFirma}
                          onChange={e => setEsSuplenteFirma(e.target.checked)}
                          style={{ marginTop: 3, width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <span>
                          <span style={{ fontWeight: 700, color: '#854d0e', fontSize: '0.9rem' }}>
                            🖋️ Suplente de firma
                          </span>
                          <span style={{ display: 'block', fontSize: '0.8rem', color: '#92400e', marginTop: 4, lineHeight: 1.5 }}>
                            Permite a este usuario firmar documentos <strong>en sustitución de la Directora Técnica</strong> cuando ella no esté disponible (baja, vacaciones, ausencia).
                            Cada firma que haga quedará registrada explícitamente como <em>"en sustitución de la Directora Técnica"</em> en el documento y en la auditoría.
                            Úsalo solo para suplencias autorizadas.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                    {form.id ? (
                      <button className="btn btn-primary" onClick={guardarPermisos} disabled={savingPermisos}>
                        {savingPermisos ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={16} />}
                        {savingPermisos ? 'Guardando...' : 'Guardar permisos'}
                      </button>
                    ) : (
                      <p style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>
                        💾 Los permisos se guardarán al crear el usuario
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cerrar</button>
          {(tabModal === 'datos' || (tabModal === 'permisos' && !form.id)) && (
            <button className="btn btn-primary" onClick={guardar} disabled={loading}>
              {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
              {form.id ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL RESET CONTRASEÑA
// ═══════════════════════════════════════════════════════════════
function ModalResetClave({ usuario, onClose, onResetado }) {
  const [nuevaClave, setNuevaClave] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    if (nuevaClave.length < 8) { setError('La clave debe tener al menos 8 caracteres'); return; }
    setLoading(true);
    try {
      const res = await api.post(`/usuarios/${usuario.id}/reset-clave`, { nueva_clave: nuevaClave });
      alert(res.data.message);
      onResetado();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={18} color="var(--color-primary)" /> Resetear contraseña
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--color-warning-bg)', borderRadius: 8, padding: 14, marginBottom: 18 }}>
            <div style={{ fontWeight: 700, color: 'var(--color-warning)', marginBottom: 4 }}>
              ⚠ Resetear contraseña de {usuario.nombre} {usuario.apellidos}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--color-warning)' }}>
              Introduce una contraseña temporal. El usuario deberá cambiarla en su próximo acceso.
            </div>
          </div>
          {error && <div className="alert alert-danger" style={{ marginBottom: 14 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Nueva contraseña temporal</label>
            <div style={{ position: 'relative' }}>
              <input type={showPass ? 'text' : 'password'} className="form-control" style={{ paddingRight: 40 }}
                placeholder="Mínimo 8 caracteres" value={nuevaClave} onChange={e => setNuevaClave(e.target.value)} autoFocus />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)' }}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--color-text-3)', lineHeight: 1.5 }}>
            💡 Comunica esta clave al usuario de forma segura (en persona o por teléfono).
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button style={{ background: 'var(--color-warning)', color: 'white' }} className="btn" onClick={handleReset} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <KeyRound size={16} />}
            Resetear contraseña
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL CREAR ROL PERSONALIZADO
// ═══════════════════════════════════════════════════════════════
function ModalNuevoRol({ onClose, onGuardado }) {
  const [form, setForm] = useState({
    nombre: '', descripcion: '',
    puede_firmar: false, puede_crear_pnt: true,
    puede_subir_archivos: true, puede_ver_todos: true, puede_exportar: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const PERMISOS = [
    { key: 'puede_firmar',         label: '✍️ Firmar documentos',       critico: true },
    { key: 'puede_crear_pnt',      label: '📄 Crear y editar PNTs' },
    { key: 'puede_subir_archivos', label: '📎 Subir archivos' },
    { key: 'puede_ver_todos',      label: '👁 Ver todos los documentos' },
    { key: 'puede_exportar',       label: '📥 Exportar e imprimir' },
  ];

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('El nombre del rol es obligatorio'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/usuarios/roles', form);
      onGuardado();
    } catch (e) { setError(e.response?.data?.error || 'Error al crear rol'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={18} color="var(--color-primary)" /> Nuevo Rol Personalizado
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger" style={{ marginBottom: 14 }}><AlertCircle size={14} /> {error}</div>}
          <div className="form-group">
            <label className="form-label">Nombre del rol <span className="required">*</span></label>
            <input className="form-control" value={form.nombre}
              onChange={e => set('nombre', e.target.value)}
              placeholder="Ej: Responsable Calidad" />
            <p style={{ fontSize: '0.73rem', color: 'var(--color-text-3)', marginTop: 4 }}>
              Se guardará como: <code>{form.nombre.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'nombre_rol'}</code>
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <input className="form-control" value={form.descripcion}
              onChange={e => set('descripcion', e.target.value)}
              placeholder="Descripción del rol (opcional)" />
          </div>
          <div className="form-group">
            <label className="form-label">Permisos del rol</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              {PERMISOS.map(p => (
                <div key={p.key} onClick={() => set(p.key, !form[p.key])} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderRadius: 8, cursor: 'pointer',
                  background: form[p.key] ? '#f0fdf4' : 'var(--color-gray-1)',
                  border: `1.5px solid ${form[p.key] ? '#86efac' : 'var(--color-gray-3)'}`,
                }}>
                  {form[p.key]
                    ? <ToggleRight size={28} color="var(--color-success)" />
                    : <ToggleLeft size={28} color="var(--color-gray-3)" />
                  }
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{p.label}</span>
                  {p.critico && <span style={{ fontSize: '0.65rem', color: 'var(--color-danger)', marginLeft: 'auto', fontWeight: 700 }}>🔒 Crítico</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Crear rol
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalReset, setModalReset] = useState(null);
  const [modalNuevoRol, setModalNuevoRol] = useState(false);
  const [tabActiva, setTabActiva] = useState('usuarios');

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/usuarios'), api.get('/usuarios/roles')])
      .then(([u, r]) => { setUsuarios(u.data); setRoles(r.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const TABS = [
    { key: 'usuarios',  label: '👥 Usuarios' },
    { key: 'permisos',  label: '🛡 Permisos por Rol' },
    { key: 'emails',    label: '✉️ Configuración de Emails' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserCog size={24} color="var(--color-primary)" /> Usuarios y Permisos
          </h1>
          <p>{usuarios.length} usuarios · Gestiona cuentas, permisos y emails del sistema</p>
        </div>
        {tabActiva === 'usuarios' && (
          <button className="btn btn-primary" onClick={() => setModal({})}>
            <Plus size={16} /> Nuevo Usuario
          </button>
        )}
        {tabActiva === 'permisos' && (
          <button className="btn btn-primary" onClick={() => setModalNuevoRol(true)}>
            <Plus size={16} /> Nuevo Rol
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--color-gray-3)' }}>
        {TABS.map(({ key, label }) => (
          <button key={key} onClick={() => setTabActiva(key)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '0.9rem', fontWeight: tabActiva === key ? 600 : 400,
            color: tabActiva === key ? 'var(--color-primary)' : 'var(--color-text-3)',
            borderBottom: tabActiva === key ? '2px solid var(--color-primary)' : '2px solid transparent',
            marginBottom: -2, fontFamily: 'var(--font)', whiteSpace: 'nowrap'
          }}>{label}</button>
        ))}
      </div>

      {/* Contenido por tab */}
      {tabActiva === 'emails' ? (
        <PanelEmails />
      ) : tabActiva === 'permisos' ? (
        <PanelPermisos roles={roles} onActualizado={load} />
      ) : (
        <div className="card">
          {loading ? <div className="loading-overlay"><div className="spinner" /></div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Usuario</th><th>Nombre completo</th><th>Email</th>
                    <th>Rol</th><th>Estado</th><th>Puede firmar</th>
                    <th>Último acceso</th><th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: ROL_COLORS[u.rol] || '#e91e8c', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>
                            {`${u.nombre?.[0] || ''}${u.apellidos?.[0] || ''}`.toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', fontWeight: 700 }}>
                              {u.username}
                              {u.id === currentUser?.id && <span style={{ fontSize: '0.68rem', color: 'var(--color-primary)', marginLeft: 4 }}>(tú)</span>}
                            </div>
                            {u.debe_cambiar_clave === 1 && (
                              <span style={{ fontSize: '0.65rem', color: 'var(--color-warning)', fontWeight: 600 }}>⚠ Debe cambiar clave</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontWeight: 500 }}>{u.nombre} {u.apellidos}</td>
                      <td style={{ fontSize: '0.82rem' }}>
                        <div>{u.email}</div>
                        {u.email_notificaciones && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Mail size={10} /> {u.email_notificaciones}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: `${ROL_COLORS[u.rol]}18`, color: ROL_COLORS[u.rol] }}>
                          {ROL_LABELS[u.rol] || u.rol}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${u.activo ? 'badge-firmado' : 'badge-archivado'}`}>
                          {u.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {u.puede_firmar
                          ? <span style={{ color: 'var(--color-success)', fontWeight: 700, fontSize: '0.8rem' }}>✅ Sí</span>
                          : <span style={{ color: 'var(--color-text-3)', fontSize: '0.8rem' }}>— No</span>}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                        {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString('es-ES') : 'Nunca'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(u)} title="Editar"><Edit size={14} /></button>
                          {u.id !== currentUser?.id && (
                            <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--color-warning)' }} onClick={() => setModalReset(u)} title="Resetear contraseña">
                              <KeyRound size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modal !== null && (
        <ModalUsuario usuario={modal?.id ? modal : null} roles={roles}
          onClose={() => setModal(null)} onGuardado={() => { setModal(null); load(); }} />
      )}
      {modalReset && (
        <ModalResetClave usuario={modalReset}
          onClose={() => setModalReset(null)} onResetado={() => { setModalReset(null); load(); }} />
      )}
      {modalNuevoRol && (
        <ModalNuevoRol
          onClose={() => setModalNuevoRol(false)}
          onGuardado={() => { setModalNuevoRol(false); load(); }} />
      )}
    </div>
  );
}
